//---------------------------------------------------------------------------------------------------
//  ?                              TRADING TRANSFORMS
//  @author         :  DeeTrade Team
//  @createdOn      :  04/10/2026
//  @description    :  Transform utilities for trading data — event mapping, feed items, node results
//---------------------------------------------------------------------------------------------------

import { degradeIdentityPart } from './positionsRegistry';
import EXIT_REASON_REGISTRY from './exitReasonRegistry.json';
import { fullRejectReason, rejectReasonLabel } from './rejectReason';

// Signal-path node types that participate in condition panels
const SIGNAL_PATH_TYPES = new Set(['comparator', 'signal', 'position_trigger']);

//! D9 — shared open/pairing key: `${sii}:${instrument}`. MANUAL sii's are NOT per-instrument
//  (every manual order for a user shares `manual:{uid}`), so bare-sii keying would collapse two open
//  manual positions on different instruments into one. Uses the SAME honest-degradation helper as the
//  positions registry so a BD row's instrument_id:None degrades to symbol / 'no-instrument', NEVER a
//  stringified "undefined". Redundant-but-harmless for ALGO (sii already embeds the instrument).
function _openSidKey(row) {
  if (!row) return null;
  return `${row.strategy_instance_id}:${degradeIdentityPart(row.instrument_id || row.symbol, 'no-instrument')}`;
}

/**
 * resolvePositionForEntry — instrument-scoped, leg-PREFERRING position lookup for an entry order
 * (D10). Replaces the bare-sii `.find()` that would (a) collapse MANUAL multi-instrument positions and
 * (b) pick an OMS-vs-BD row by array order. Prefers the OMS-tracked (non-BD) leg — it carries SL/TP/
 * lifecycle context BD never does — falling back to a BD row only when no OMS row exists for this
 * exact (sii, instrument).
 * @param {object[]} positions
 * @param {object|null} entryOrder
 * @returns {object|null}
 */
export function resolvePositionForEntry(positions, entryOrder) {
  if (!entryOrder || !positions) return null;
  const sii = entryOrder.strategy_instance_id;
  const instr = entryOrder.instrument_id || entryOrder.symbol;
  const sameSiiAndInstrument = (p) =>
    p.strategy_instance_id === sii && (p.instrument_id || p.symbol) === instr;
  return (
    positions.find((p) => sameSiiAndInstrument(p) && (p.leg || 'BD') !== 'BD') ||
    positions.find(sameSiiAndInstrument) ||
    null
  );
}

// INR currency formatter (locale: en-IN, no trailing decimals for whole numbers)
const _inrFmt = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

// --> Exit reason display mapping -- DERIVED from the exit-reason registry (the one authority);
//*   chart marker text and trade history share this wording.
const EXIT_REASON_MAP = Object.fromEntries(
  Object.entries(EXIT_REASON_REGISTRY).map(([reason, row]) => [reason, row.label]),
);

//--> live-exit-lane R3.5.1 (W6) — human-readable labels for the 4-way diagnosis classification
//*   (`FillExpectationWatchdog`, order-management). Consumed by OrdersTable's chase/diagnosis
//*   tooltip (never the raw enum string in a title=). Exported so OrdersTable and any future
//*   diagnosis-state sink share ONE vocabulary (mirrors the LEG_TYPE_DISPLAY / EXIT_REASON_MAP
//*   pattern already established in this file — no second, divergent label set).
const DIAGNOSIS_STATE_LABELS = {
  trigger_not_reached: 'Trigger Not Reached',
  dispatched_not_hit: 'Dispatched, Not Hit',
  broker_rejected_stuck: 'Broker Rejected — Stuck',
  broker_truth_unknown: 'Broker Truth Unknown',
  //--> R5.2's distinct price-gate state rides the SAME DIAGNOSIS_ANNOTATED event/column shape
  //*   (02-design.md R3.5.1/R5.2, mutually exclusive with the 4 states above) — included here so
  //*   a row that happens to carry it renders a human label too, never the raw enum.
  price_untrusted_not_dispatched: 'Price Untrusted — Not Dispatched',
};

// --> Leg type display mapping
const LEG_TYPE_DISPLAY = {
  entry: 'Entry',
  exit: 'Exit',
  stop_loss: 'Stop Loss',
  take_profit: 'Take Profit',
  time_exit: 'Time Exit',
  rule_exit: 'Signal Exit',
  rollover_exit: 'Rollover',
  //--> money-path #30 D4 — see EXIT_REASON_MAP comment above; distinct label for the
  //*   pinned-instrument forced T-1 pre-expiry close (R3.3), never the generic exit/rollover.
  pinned_expiry_exit: 'Pinned Expiry Exit',
  flip_exit: 'Flip Exit',
  //--> manual-exit-netting-p0 R3.2 — the new manual-exit leg (R1) + the operator-repair leg (R2).
  //*   Without these, the Orders table / drawer would render the raw enum string ("manual_exit") or
  //*   a blank badge. Drift-locked by tradingTransforms.legTypeCoverage.test.js against the backend
  //*   LegType enum. corrective was a PRE-EXISTING gap now user-visible (R2 repair orders appear on
  //*   the same surface).
  manual_exit: 'Manual Exit',
  corrective: 'Corrective',
  //--> Fallback: exit_reason values for legacy orders without leg_type
  sl: 'Stop Loss',
  tp: 'Take Profit',
  exit_long: 'Exit Long',
  exit_short: 'Exit Short',
};

//--> Prefer order.bar_start (fill-bar / bar-clock timestamp) over order._epoch
//*   (wall-clock event-publish time). bar_start is what trigger_consumer
//*   advances to the fill-tick's bar under tick-mode paper fills — so using
//*   it anchors chart markers and bracket bands to the correct candle.
//*   Defensive: falls back through the chain (bar_start → _epoch → timestamp)
//*   so legacy orders without bar_start still render.
function _barStartOrEpoch(order) {
  if (!order) return null;
  if (order.bar_start) {
    const t = new Date(order.bar_start).getTime();
    if (Number.isFinite(t)) return t;
  }
  return order._epoch || null;
}

// --> Format duration between two timestamps
function _formatDuration(startMs, endMs) {
  if (!startMs || !endMs) return null;
  const diffMs = endMs - startMs;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
}

// IST time formatter (hh:mm in Asia/Kolkata, 24-hr for compactness)
const _istTimeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

// --> 10509-12 E13 / R10 — conversion labels require broker-audit precision to seconds.
const _istSecondTimeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  //! hourCycle h23, not hour12:false — ICU renders midnight as "24:xx" under hour12:false.
  hourCycle: 'h23',
});

//--> Date+time formatter for legs NOT from the current IST trading day (mirrors
//*   OrdersTable.jsx's formatTime — a bare "HH:MM" gives no clue which day a
//*   leg belongs to; GRAVIN/ASIPAI trade cards with entryTime from Jul-1/Jul-2
//*   rendered bare "HH:MM" and were mistaken for "today").
const _istDateTimeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

// IST calendar date key (YYYY-MM-DD) for same-day comparison (mirrors OrdersTable.jsx).
const _istDateKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const _istDateKey = (d) => _istDateKeyFmt.format(d);

/**
 * Format a price value as INR string with ₹ prefix.
 * @param {number|string|null} price
 * @returns {string}
 */
const _fmtPrice = (price) => {
  if (price == null || price === '') return '—';
  const num = typeof price === 'number' ? price : parseFloat(price);
  if (isNaN(num)) return String(price);
  return _inrFmt.format(num);
};

/**
 * Format a UTC ISO timestamp for the Trades tab: bare IST HH:MM when the
 * timestamp falls on the current IST trading day; "DD Mon, HH:MM" otherwise
 * (a leg from a prior day must never render as an undated bare time — see
 * OrdersTable.jsx's identical today-vs-dated rule for the Orders tab).
 * @param {string} timestamp - ISO 8601 string
 * @returns {string}
 */
const _fmtTimeIST = (timestamp) => {
  if (!timestamp) return '';
  try {
    const d = new Date(timestamp);
    if (_istDateKey(d) === _istDateKey(new Date())) {
      return _istTimeFmt.format(d);
    }
    return _istDateTimeFmt.format(d);
  } catch {
    return timestamp;
  }
};

/**
 * formatExitConversion — render the audit-derived stop-to-limit conversion label.
 * `exit_reason` remains separate from `leg_type`: the label uses the true reason while the
 * protective leg keeps its Stop Loss taxonomy for order/position behavior.
 * @param {object|null} order
 * @returns {string|null}
 */
const formatExitConversion = (order) => {
  const conversion = order?.exit_conversion;
  if (!conversion) return null;

  const reasonKey = conversion.reason || order.exit_reason;
  const reasonLabel = EXIT_REASON_MAP[reasonKey] || LEG_TYPE_DISPLAY[reasonKey] || 'Exit';
  const details = [];
  if (conversion.prior_trigger_price != null) {
    details.push(`trigger ${_fmtPrice(conversion.prior_trigger_price)}`);
  }
  if (conversion.converted_at) {
    const convertedAt = new Date(conversion.converted_at);
    if (!isNaN(convertedAt.getTime())) {
      details.push(`${_istSecondTimeFmt.format(convertedAt)} IST`);
    }
  }
  const suffix = details.length > 0 ? ` (${details.join(', ')})` : '';
  return `${reasonLabel} — converted from Stop Loss${suffix}`;
};

// =============================================================================
// 1. mapEventTypeToStatus
// =============================================================================

/**
 * Maps a WS event_type string to a display-friendly order status.
 *
 * @param {string} eventType - e.g. 'ORDER_CREATED', 'ORDER_FILLED'
 * @returns {string} Display status: 'PENDING' | 'SUBMITTED' | 'FILLED' | 'REJECTED' | 'UNKNOWN'
 */
export const mapEventTypeToStatus = (eventType) => {
  switch (eventType) {
    case 'ORDER_CREATED':   return 'PENDING';
    case 'ORDER_SUBMITTED': return 'SUBMITTED';
    case 'ORDER_FILLED':    return 'FILLED';
    case 'ORDER_REJECTED':  return 'REJECTED';
    case 'ORDER_CANCELLED': return 'CANCELLED';
    //! LV-M4 (2026-07-06) — fallback cases for payloads lacking an explicit `status`.
    //  An amended order is still working at the broker; a partial fill keeps working too.
    case 'ORDER_AMENDED':          return 'SUBMITTED';
    case 'ORDER_PARTIALLY_FILLED': return 'PARTIALLY_FILLED';
    //! fix/flattrade-trigger-pending-unmapped — a resting order at the broker is
    //  distinct from a dead one; dormant defensive-path (the primary WS payload
    //  always carries an explicit `status`, so this fallback is off the hot path,
    //  but without it a future status-less OPEN payload would silently downgrade
    //  to 'UNKNOWN' instead of surfacing as OPEN).
    case 'ORDER_OPEN':             return 'OPEN';
    default:                return 'UNKNOWN';
  }
};

// =============================================================================
// 1b. resolveProductLabel
// =============================================================================

/**
 * Maps a product_type (canonical OMS terms CNC/MIS/NRML OR broker vocabulary
 * "Cash"/"eatm"/"Margin"/"btst") to the human trading label shown on the
 * Orders surface. Unknown/absent → '' (render nothing, never a raw code).
 *
 * @param {string|null|undefined} productType
 * @returns {string} 'Delivery' | 'Intraday' | 'Carryforward' | 'Margin' | 'BTST' | ''
 */
export const resolveProductLabel = (productType) => {
  //! R2.1 — reconciles TWO value spaces through one label table: OMS canonical
  //!  (DELIVERY/INTRADAY/CARRYFORWARD/MARGIN/UNKNOWN, order_taxonomy/enums.py:55-64) AND broker-form
  //!  (CNC/CASH/MIS/EATM/NRML/MARGIN/BTST). NRML and CARRYFORWARD are the SAME product at the wire
  //!  (flattrade.md:80: CARRYFORWARD→M=NRML) → both label 'Carryforward'; MARGIN (MTF) is genuinely
  //!  distinct. UNKNOWN falls to the default '' (R2.2).
  switch (String(productType || '').trim().toUpperCase()) {
    case 'CNC':
    case 'CASH':
    case 'DELIVERY':       return 'Delivery';
    case 'MIS':
    case 'EATM':
    case 'INTRADAY':       return 'Intraday';
    case 'NRML':
    case 'CARRYFORWARD':   return 'Carryforward';
    case 'MARGIN':         return 'Margin';
    case 'BTST':           return 'BTST';
    default:               return '';
  }
};

// =============================================================================
// 2. deriveInstrumentId
// =============================================================================

/**
 * Extracts the instrument_id from a composite strategy instance key.
 *
 * Key format: "userId:strategyId:versionId:instrumentId:timeframe"
 * instrumentId may itself contain colons (e.g., "NSE:NIFTY24APRFUT"), so:
 *   - Parts [0..2] → userId, strategyId, versionId
 *   - Part  [-1]   → timeframe
 *   - Parts [3..-2] joined by ':' → instrumentId
 *
 * @param {string|null|undefined} strategyInstanceId
 * @returns {string} instrument_id or '' on invalid input
 */
export const deriveInstrumentId = (strategyInstanceId) => {
  if (!strategyInstanceId) return '';

  const parts = strategyInstanceId.split(':');
  //* Need at least 5 segments: userId, stratId, verId, instrumentId, timeframe
  if (parts.length < 5) return '';

  //--> Slice off first 3 (userId/stratId/verId) and last 1 (timeframe)
  const instrumentParts = parts.slice(3, parts.length - 1);
  return instrumentParts.join(':');
};

// =============================================================================
// 3. transformNodeResults
// =============================================================================

/**
 * Filters node execution results to the signal-path only and returns
 * condition display objects for the SignalEvaluationPanel.
 *
 * Signal-path node types: 'comparator', 'signal', 'position_trigger'
 *
 * When hasPosition=false → keep action_type='entry' nodes (scanning state)
 * When hasPosition=true  → keep action_type='exit'  nodes (in-trade state)
 *
 * @param {Object|Array|null|undefined} nodeResults - Node result objects.
 *   As Object (from WS): { nodeId: { type, action_type, label, value, triggered }, ... }
 *   As Array (legacy): [{ type, action_type, name, value, isMet }, ...]
 * @param {boolean} hasPosition - Whether the strategy currently holds a position
 * @returns {Array<{ name: string, value: string, isMet: boolean }>}
 */
export const transformNodeResults = (nodeResults, hasPosition) => {
  if (!nodeResults) return [];

  //--> Normalize: Object keyed by nodeId → Array of entries
  let entries;
  if (Array.isArray(nodeResults)) {
    //* Legacy array format (from tests or pre-processed data)
    entries = nodeResults;
  } else if (typeof nodeResults === 'object') {
    //* Object format from WS: strategy.execution.{id} → { node_results: { nodeId: {...} } }
    const keys = Object.keys(nodeResults);
    if (keys.length === 0) return [];
    entries = keys.map((nodeId) => ({
      ...nodeResults[nodeId],
      name: nodeResults[nodeId].label || nodeResults[nodeId].name || nodeId,
      isMet: nodeResults[nodeId].value === true || nodeResults[nodeId].triggered === true,
    }));
  } else {
    return [];
  }

  //--> Determine which action path to surface
  const targetAction = hasPosition ? 'exit' : 'entry';

  return entries
    .filter((node) =>
      //* Only signal-path types participate in condition panels
      SIGNAL_PATH_TYPES.has(node.type) &&
      node.action_type === targetAction
    )
    .map((node) => ({
      name:  node.name || node.label || '',
      value: typeof node.value === 'boolean' ? (node.value ? 'TRUE' : 'FALSE') : String(node.value ?? ''),
      isMet: node.isMet === true || node.value === true || node.triggered === true,
      type: node.type || '',
      action: node.action || (node.data && node.data.action) || '',
    }));
};

// =============================================================================
// 4b. BD (broker-direct) FIFO trade-pairing — Phase 1
// =============================================================================
//
// Broker-direct (external) orders arrive as a flat, unpaired list — the broker
// order book carries no entry/exit linkage. Before this engine every EXECUTED
// BD row rendered as its own degenerate single-leg "trade" (an intraday BUY and
// its closing SELL showed as two unrelated cards, the SELL even labelled
// SHORT). This reconstructs real round trips with a signed-qty FIFO lot walk,
// bucketed so DELIVERY and INTRADAY quantities (and unrecognised product
// codes) can never net against each other.
//
//! FIFO (oldest-open-lot-first) is the SEBI/tax-standard matching convention
//! for Indian equity square-off attribution — a correctness requirement, not
//! a style choice. It is implemented literally here (see _fifoWalkBdBucket).

//--> Executed-status vocabulary (case-insensitive) that makes a BD row eligible
//*   for FIFO pairing. Anything else (CANCELLED, REJECTED, PENDING, ...) keeps
//*   today's single-row degenerate card — never guessed into a pairing.
const BD_EXECUTED_STATUSES = new Set(['EXECUTED', 'FILLED', 'COMPLETE', 'COMPLETED']);

//--> Product-class buckets for BD FIFO pairing (Phase 1). DELIVERY can carry
//*   overnight; INTRADAY is squared off same-session. NRML is folded into
//*   INTRADAY here for consistency with resolveProductLabel (NRML/MARGIN share
//*   the 'Margin' display label there) — an explicit, documented extension of
//*   the given vocab, still a distinct set that never merges with DELIVERY.
//*   Anything unrecognised gets its OWN bucket keyed by the raw value (see
//*   _bdProductClass) so an unknown code can never silently net against a
//*   known class.
const BD_DELIVERY_PRODUCTS = new Set(['CNC', 'CASH', 'BTST']);
const BD_INTRADAY_PRODUCTS = new Set(['MIS', 'MARGIN', 'EATM', 'NRML']);

//! (product, trading-DAY, orders) bucketing — user's model 2026-07-06.
//*   Genuinely same-session products square off within one trading day, so their
//*   BD legs must pair WITHIN a day (a leg with no same-day partner is a carry —
//*   e.g. a Margin lot converted to BTST and held — which correctly drops out as
//*   the held position instead of being closed by the NEXT day's opposite fill,
//*   the FIFO-eats-the-converted-lot bug that showed a −9/22h "intraday" trade).
//*   NRML is EXCLUDED: it is carry-forward F&O (legitimately multi-day), so it
//*   pairs across days like DELIVERY even though it never nets WITH delivery.
//*   DELIVERY (CNC/Cash/BTST) is NOT day-scoped — a holding spans days.
const BD_SAME_SESSION_PRODUCTS = new Set(['MIS', 'MARGIN', 'EATM']);

/**
 * _bdProductClass — DELIVERY | INTRADAY | OTHER:<RAW> (case-insensitive).
 * Unknown/absent product codes get their own bucket so they never net
 * against a recognised class.
 */
function _bdProductClass(productType) {
  const key = String(productType || '').trim().toUpperCase();
  if (BD_DELIVERY_PRODUCTS.has(key)) return 'DELIVERY';
  if (BD_INTRADAY_PRODUCTS.has(key)) return 'INTRADAY';
  return key ? `OTHER:${key}` : 'OTHER:__NONE__';
}

/**
 * _isExecutedBdRow — true when a BD row is genuinely fillable and pairing-eligible:
 * executed status, recognised side, positive numeric qty, resolvable price.
 * Anything short of that (CANCELLED, missing price, zero/garbage qty) keeps
 * today's single-row degenerate rendering rather than corrupting the lot walk.
 */
function _isExecutedBdRow(row) {
  if (!row) return false;
  const status = String(row.status || '').toUpperCase();
  if (!BD_EXECUTED_STATUSES.has(status)) return false;
  if (row.side !== 'BUY' && row.side !== 'SELL') return false;
  const qty = parseFloat(row.qty);
  if (!(qty > 0)) return false;
  const priceRaw = row.filled_price ?? row.price;
  if (priceRaw === null || priceRaw === undefined || priceRaw === '') return false;
  return Number.isFinite(parseFloat(priceRaw));
}

//--> BD rows may lack _epoch (REST rows) — same fallback chain as step 0 above,
//*   kept local so this section has no ordering dependency on that loop.
function _bdRowEpoch(row) {
  if (row._epoch) return row._epoch;
  return row.created_at ? new Date(row.created_at).getTime() : 0;
}

/**
 * _bdBucketMeta — derives the FIFO bucket key for a BD row:
 * (broker | broker_client_id | exchange:symbol | productClass), case-insensitive
 * on broker/exchange/symbol. Never nets DELIVERY against INTRADAY, and never
 * nets one account/broker against another.
 */
function _bdBucketMeta(row) {
  const broker = String(row.broker || '').toLowerCase();
  const brokerClientId = String(row.broker_client_id || '');
  const exchange = String(row.exchange || '').toUpperCase();
  const symbol = String(row.symbol || '').toUpperCase();
  const productClass = _bdProductClass(row.product_type);
  //! (product, DAY, orders) — same-session products get the IST trading-day
  //*  appended to the bucket key so their legs pair ONLY within a day (user's
  //*  model 2026-07-06). NRML/DELIVERY are NOT day-scoped (carry-forward /
  //*  multi-day holdings). The day key is derived from the row's fill time
  //*  (_bdRowEpoch → IST calendar day); a row with no resolvable time falls
  //*  back to a stable '__noday__' partition so it never silently merges into
  //*  an arbitrary day.
  const rawProduct = String(row.product_type || '').trim().toUpperCase();
  let dayKey = '';
  if (BD_SAME_SESSION_PRODUCTS.has(rawProduct)) {
    const epoch = _bdRowEpoch(row);
    dayKey = epoch ? `|${_istDateKey(new Date(epoch))}` : '|__noday__';
  }
  return {
    key: `${broker}|${brokerClientId}|${exchange}:${symbol}|${productClass}${dayKey}`,
    broker,
    brokerClientId,
    exchange,
    symbol,
    productClass,
  };
}

//--> D-G3-2 — a pair's net comes from the order-view head (last exit row); no head = unresolved.
const _brokerDirectNet = (members) => (members.some((o) => o && o.is_lifecycle_head === true)
  ? readLifecycleNet(members)
  : BROKER_DIRECT_UNRESOLVED_NET);

/**
 * _fifoWalkBdBucket — signed-qty FIFO lot walk for ONE bucket's executed rows
 * (pre-sorted ascending by time). BUY=+qty, SELL=-qty. Oldest open lot(s)
 * close first; a single exit row can close several lots (SELL 60 vs 3×BUY 20);
 * a single lot can be closed across several exit rows (partial fills). A
 * "closing streak" — one or more consecutive opposite-direction rows since the
 * last direction change — becomes ONE trade group: entryPrice/exitPrice are
 * qty-weighted averages across whichever lots/rows contributed. A direction
 * flip mid-row (exit qty exceeds all available open lots) finalises the
 * current streak and opens a brand-new lot in the flipped direction from the
 * leftover qty (e.g. a SELL beyond open long qty opens a SHORT remainder lot).
 *
 * @returns {{ closedGroups: Array, remainderLots: Array }}
 */
function _fifoWalkBdBucket(rows, productClass) {
  const lots = []; // { remaining, price, time, side, entryRow }
  const closedGroups = [];
  //! read-lane-correctness R5.1 — DELIVERY-class holdings SALES (a SELL with no open lot to close).
  //*  In the NSE/BSE cash/equity DELIVERY segment there is NO legal overnight naked short — a delivery
  //*  short must be covered by existing demat holdings (or T+1 BTST); the only sanctioned overnight
  //*  short is SLB, a separate product, never a market SELL. So a DELIVERY SELL with no prior open lot
  //*  is ALWAYS a holdings sale, NEVER the opening of a short — it must not be queued as an openable
  //*  lot for a later BUY to net into a fabricated closed SHORT round trip with P&L.
  const unpairedSaleGroups = []; // { remaining, price, time, side, entryRow }
  let batch = null; // { entryPortions: [{price, qty, time, entryRow}], exitPortions: [{row, qty, price, time}] }

  const finalizeBatch = () => {
    if (!batch) return;
    const { entryPortions, exitPortions } = batch;
    batch = null;
    if (entryPortions.length === 0 || exitPortions.length === 0) return;

    const entryQty = entryPortions.reduce((s, p) => s + p.qty, 0);
    const entryPrice = entryPortions.reduce((s, p) => s + p.qty * p.price, 0) / entryQty;
    const exitQty = exitPortions.reduce((s, p) => s + p.qty, 0);
    const exitPrice = exitPortions.reduce((s, p) => s + p.qty * p.price, 0) / exitQty;
    const firstEntry = entryPortions.reduce((a, b) => (a.time <= b.time ? a : b));
    const lastExit = exitPortions.reduce((a, b) => (a.time >= b.time ? a : b));
    const side = firstEntry.entryRow.side === 'BUY' ? 'LONG' : 'SHORT';
    //! 144-6 R7-T4 / D-G3-2 — NO browser P&L here: this walk only GROUPS the legs. Gross, charges
    //  and net of a broker-direct pair are computed once in order-view (consolidated_view pairing)
    //  and arrive on the pair head (the last exit row); the group reads them via readLifecycleNet.

    //--> Distinct exit rows, insertion order (a lot closed across multiple rows
    //*   must not duplicate the same row if it contributed more than one portion).
    const seen = new Set();
    const bracketOrders = [];
    for (const p of exitPortions) {
      const rid = p.row.order_id || p.row.id;
      if (seen.has(rid)) continue;
      seen.add(rid);
      bracketOrders.push(p.row);
    }

    closedGroups.push({
      side, entryPrice, exitPrice, qty: entryQty,
      entryTime: firstEntry.time, exitTime: lastExit.time,
      entryOrder: firstEntry.entryRow, bracketOrders,
      lifecycleNet: _brokerDirectNet([firstEntry.entryRow, ...bracketOrders]),
    });
  };

  for (const row of rows) {
    const rowQty = parseFloat(row.qty);
    const rowPrice = parseFloat(row.filled_price ?? row.price);
    const rowTime = _bdRowEpoch(row);

    //! R5.1 — DELIVERY-class SELL with NO open lot = a holdings sale (see the unpairedSaleGroups note
    //!  above). Peel it off BEFORE the isOpening check so it is NEVER queued as an openable lot a later
    //!  BUY could net into a fabricated closed SHORT. Scoped to lots.length===0 (a mid-walk oversized
    //!  SELL that flips is the SAME market-structure violation but is OUT of this fix's literal AC
    //!  scope — see 02-design.md Open question 3).
    if (productClass === 'DELIVERY' && row.side === 'SELL' && lots.length === 0) {
      finalizeBatch(); // a standalone sale always ends any pending closing streak
      unpairedSaleGroups.push({ remaining: rowQty, price: rowPrice, time: rowTime, side: row.side, entryRow: row });
      continue;
    }

    //--> Opening: queue empty, or front lot shares this row's side (extends the
    //*   current position). Otherwise this row is a CLOSING action (opposite side).
    const isOpening = lots.length === 0 || lots[0].side === row.side;

    if (isOpening) {
      finalizeBatch(); // an opening row always ends any pending closing streak
      lots.push({ remaining: rowQty, price: rowPrice, time: rowTime, side: row.side, entryRow: row });
      continue;
    }

    if (!batch) batch = { entryPortions: [], exitPortions: [] };
    let remainingRowQty = rowQty;
    let closedByThisRow = 0;

    while (remainingRowQty > 1e-9 && lots.length > 0 && lots[0].side !== row.side) {
      const front = lots[0];
      const matchQty = Math.min(front.remaining, remainingRowQty);
      batch.entryPortions.push({ price: front.price, qty: matchQty, time: front.time, entryRow: front.entryRow });
      front.remaining -= matchQty;
      remainingRowQty -= matchQty;
      closedByThisRow += matchQty;
      if (front.remaining <= 1e-9) lots.shift();
    }

    if (closedByThisRow > 1e-9) {
      batch.exitPortions.push({ row, qty: closedByThisRow, price: rowPrice, time: rowTime });
    }

    if (remainingRowQty > 1e-9) {
      //--> Flip: this row's leftover qty (after exhausting every opposite lot)
      //*   opens a brand-new lot in the flipped direction. The closing streak
      //*   that just ended is unrelated to this new lot — finalise it first.
      finalizeBatch();
      lots.push({ remaining: remainingRowQty, price: rowPrice, time: rowTime, side: row.side, entryRow: row });
    }
  }

  finalizeBatch(); // end of bucket data: finalise any dangling closing streak
  return { closedGroups, remainderLots: lots, unpairedSaleGroups };
}

/**
 * _isBrokerTruthPosition — true when an openPositions[] entry is a
 * broker-reported position row (order-view consolidated view), as opposed to
 * a platform ALGO strategy position. The consolidated view's
 * _map_broker_order_row_to_consolidated maps a broker POSITION row's status to
 * the literal string 'POSITION' (backend/order-view sweep_service.py
 * status_rank=5 comment) — the single most reliable signal. The synthetic
 * broker_order_id 'POS:{symbol}...' prefix is a defensive fallback for callers
 * that strip status.
 */
function _isBrokerTruthPosition(pos) {
  if (!pos) return false;
  if (String(pos.status || '').toUpperCase() === 'POSITION') return true;
  const oid = String(pos.order_id || pos.broker_order_id || '');
  return oid.startsWith('POS:');
}

//--> Does a broker-truth position match this bucket's remainder (same
//*   instrument/account, opposite-agnostic side+qty)? side is already 'LONG'/
//*   'SHORT' on both sides of the comparison (broker positions carry side as
//*   sign-derived LONG/SHORT, never BUY/SELL — see sweep_service.py).
function _bdPositionMatchesRemainder(pos, side, qty, symbol, exchange, broker, brokerClientId) {
  const pSymbol = String(pos.symbol || pos.instrument_symbol || '').toUpperCase();
  const pExchange = String(pos.exchange || '').toUpperCase();
  if (pSymbol !== symbol || (exchange && pExchange && pExchange !== exchange)) return false;
  if (broker && pos.broker && String(pos.broker).toLowerCase() !== broker) return false;
  if (brokerClientId && pos.broker_client_id && String(pos.broker_client_id) !== brokerClientId) return false;
  const pQty = Math.abs(parseFloat(pos.qty ?? pos.quantity ?? 0));
  if (!Number.isFinite(pQty) || Math.abs(pQty - qty) > 1e-6) return false;
  return String(pos.side || '').toUpperCase() === side;
}

/**
 * _resolveBdAnchor — resolve a bucket's unmatched remainder against
 * broker-truth positions:
 *  (a) same-class match                                → plain open group.
 *  (b) INTRADAY remainder, no same-class match, but a matching DELIVERY-class
 *      broker position exists (side+qty)                → same-day MIS→CNC-style
 *      conversion: close the group, exitReason 'Converted to delivery'.
 *  (c) no clean match, but SOME broker-truth position data is identifiable
 *                                                        → needsReconcile: true
 *      (never guess a pairing).
 *  (d) no broker-truth position data identifiable at all → degrade gracefully,
 *      plain open group, no flags.
 */
function _resolveBdAnchor(remainderGroup, meta, side, qty, openPositions, hasAnyBrokerTruth) {
  const brokerTruthPositions = (openPositions || []).filter(_isBrokerTruthPosition);
  const candidates = brokerTruthPositions.filter((p) =>
    _bdPositionMatchesRemainder(p, side, qty, meta.symbol, meta.exchange, meta.broker, meta.brokerClientId)
  );

  const sameClassMatch = candidates.find((p) => _bdProductClass(p.product_type) === meta.productClass);
  if (sameClassMatch) return remainderGroup;

  if (meta.productClass === 'INTRADAY') {
    const deliveryMatch = candidates.find((p) => _bdProductClass(p.product_type) === 'DELIVERY');
    if (deliveryMatch) {
      return {
        ...remainderGroup,
        status: 'closed',
        exitReason: 'Converted to delivery',
        pnl: null,
        converted: true,
      };
    }
  }

  if (hasAnyBrokerTruth) {
    return { ...remainderGroup, needsReconcile: true };
  }

  return remainderGroup;
}

/**
 * _buildBdPairedGroups — buckets EXECUTED BD rows and runs the FIFO lot walk
 * per bucket, returning closed round-trip trade groups plus (at most) one
 * open remainder group per bucket, anchored against openPositions.
 */
function _buildBdPairedGroups(rows, openPositions) {
  const buckets = new Map(); // key -> { meta, rows: [] }
  for (const row of rows) {
    const meta = _bdBucketMeta(row);
    if (!buckets.has(meta.key)) buckets.set(meta.key, { meta, rows: [] });
    buckets.get(meta.key).rows.push(row);
  }

  const hasAnyBrokerTruth = (openPositions || []).some(_isBrokerTruthPosition);
  const groups = [];

  for (const [bucketKey, { meta, rows: bucketRows }] of buckets) {
    //--> Copy before sorting — never mutate the caller's legacyOrders bucket.
    const sorted = [...bucketRows].sort((a, b) => _bdRowEpoch(a) - _bdRowEpoch(b));
    //! R5.1 — thread the bucket's productClass so a DELIVERY-class SELL-first sale is peeled off
    //!  honestly instead of netting into a fabricated closed SHORT. productClass is constant across a
    //!  bucket (it is part of the bucket key, _bdBucketMeta).
    const { closedGroups, remainderLots, unpairedSaleGroups } = _fifoWalkBdBucket(sorted, meta.productClass);

    closedGroups.forEach((cg, idx) => {
      groups.push({
        id: `bd-fifo-${bucketKey}-${idx}`,
        tradeNumber: null,
        entryOrder: cg.entryOrder,
        bracketOrders: cg.bracketOrders,
        //--> D1: lift instrument fields to trade-group level (same pattern as ALGO/§4a).
        //*   order-instrument-identity-normalization P2b — display_symbol (the stored canonical
        //*   instrument-master name) now wins first; falls back to the #4b read-lane `symbol`
        //*   resolve, then `underlying_symbol` (order-instrument-identity-surfacing, see §4a) for
        //*   un-backfilled/F&O rows, then `name`.
        symbol:   cg.entryOrder.display_symbol ?? cg.entryOrder.symbol   ?? cg.entryOrder.underlying_symbol   ?? cg.entryOrder.name   ?? null,
        name:     cg.entryOrder.name     ?? null,
        exchange: cg.entryOrder.exchange ?? null,
        status: 'closed',
        exitReason: 'Closed',
        side: cg.side,
        entryPrice: cg.entryPrice,
        exitPrice: cg.exitPrice,
        pnl: cg.lifecycleNet.net,
        lifecycleNet: cg.lifecycleNet,
        qty: cg.qty,
        contractMultiplier: 1,
        entryTime: cg.entryTime,
        exitTime: cg.exitTime,
        duration: _formatDuration(cg.entryTime, cg.exitTime),
        isBrokerDirect: true,
        bdPaired: true, // distinguishes a real paired trade from a single-row degenerate card
        source_tag: cg.entryOrder.source_tag ?? 'EXTERNAL',
      });
    });

    if (remainderLots.length > 0) {
      const totalQty = remainderLots.reduce((s, l) => s + l.remaining, 0);
      const entryPrice = remainderLots.reduce((s, l) => s + l.remaining * l.price, 0) / totalQty;
      const firstLot = remainderLots.reduce((a, b) => (a.time <= b.time ? a : b));
      const side = firstLot.side === 'BUY' ? 'LONG' : 'SHORT';

      let remainderGroup = {
        id: `bd-fifo-remainder-${bucketKey}`,
        tradeNumber: null,
        entryOrder: firstLot.entryRow,
        bracketOrders: [],
        //*   order-instrument-identity-normalization P2b — display_symbol first (see closedGroups above).
        symbol:   firstLot.entryRow.display_symbol ?? firstLot.entryRow.symbol   ?? firstLot.entryRow.underlying_symbol   ?? firstLot.entryRow.name   ?? null,
        name:     firstLot.entryRow.name     ?? null,
        exchange: firstLot.entryRow.exchange ?? null,
        status: 'open',
        exitReason: null,
        side,
        entryPrice,
        exitPrice: null,
        pnl: null,
        qty: totalQty,
        contractMultiplier: 1,
        entryTime: firstLot.time,
        exitTime: null,
        duration: null,
        isBrokerDirect: true,
        bdPaired: true,
        bdRemainder: true,
        source_tag: firstLot.entryRow.source_tag ?? 'EXTERNAL',
      };

      remainderGroup = _resolveBdAnchor(remainderGroup, meta, side, totalQty, openPositions, hasAnyBrokerTruth);
      groups.push(remainderGroup);
    }

    //! read-lane-correctness R5.1 — render any DELIVERY-class holdings SALES as ONE honest unpaired
    //!  group per bucket (same weighted-avg / qty-sum / first-last-time aggregation idiom as the
    //!  remainder block above). Load-bearing safety choices:
    //*   - status:'closed' (NOT 'open') — routes to TradeCard's CLOSED branch, which reads trade.pnl
    //*     STATICALLY. An 'open' status with any non-LONG side would let TradeCard.livePnl compute a
    //*     FABRICATED running sale P&L from ltp — silently reintroducing the exact defect this removes.
    //*   - exitTime = the sale's LAST fill time (NOT null) — filterClosedTradesToDisplayRange keeps any
    //*     closed group with !exitTime in EVERY window forever; a real exitTime date-scopes it (F2).
    //*   - side:'SELL' (NOT 'SHORT') — the honest literal action, never a fabricated open-short
    //*     direction. TradeCard renders 'SELL' NEUTRAL (F6).
    //*   - pnl:null / exitPrice:null / exitReason:'Holdings sale' — never a fabricated P&L.
    if (unpairedSaleGroups.length > 0) {
      const totalSaleQty = unpairedSaleGroups.reduce((s, l) => s + l.remaining, 0);
      const avgSalePrice = unpairedSaleGroups.reduce((s, l) => s + l.remaining * l.price, 0) / totalSaleQty;
      const firstSale = unpairedSaleGroups.reduce((a, b) => (a.time <= b.time ? a : b));
      const lastSale = unpairedSaleGroups.reduce((a, b) => (a.time >= b.time ? a : b));
      groups.push({
        id: `bd-fifo-holdingssale-${bucketKey}`,
        tradeNumber: null,
        entryOrder: firstSale.entryRow,
        bracketOrders: [],
        //*   order-instrument-identity-normalization P2b — display_symbol first (see closedGroups above).
        symbol:   firstSale.entryRow.display_symbol ?? firstSale.entryRow.symbol   ?? firstSale.entryRow.underlying_symbol   ?? firstSale.entryRow.name   ?? null,
        name:     firstSale.entryRow.name     ?? null,
        exchange: firstSale.entryRow.exchange ?? null,
        status: 'closed',
        exitReason: 'Holdings sale',
        side: 'SELL',
        entryPrice: avgSalePrice,
        exitPrice: null,
        pnl: null,
        qty: totalSaleQty,
        contractMultiplier: 1,
        entryTime: firstSale.time,
        exitTime: lastSale.time,
        duration: null,
        isBrokerDirect: true,
        bdPaired: true,
        bdHoldingsSale: true,
        source_tag: firstSale.entryRow.source_tag ?? 'EXTERNAL',
      });
    }
  }

  return groups;
}

// =============================================================================
// 4c. Charges + lifecycle net READERS (144-6 R7) — display only, NO arithmetic
// =============================================================================
//* Every charge / net figure is derived server-side from the one charges authority (OMS compute at
//* read, order-view broker-truth overlay). These helpers only READ the named API fields so the three
//* order surfaces (Orders page, Monitoring tab, Live Analysis) cannot drift apart or fall back to a
//* silent blank / 0.

//--> Short reason phrases for charges_unavailable_reason / net_unavailable_reason (callers compose
//    "Unavailable - x" / "Unresolved (x)"). `no_schedule_row:<key>` carries its key after the colon.
export const CHARGE_REASON_PHRASES = {
  legacy_no_realized_delta: 'pre-release trade',
  no_lifecycle: 'pre-release trade',
  realized_attribution_missing: 'attribution missing',
  schedule_snapshot_unavailable: 'schedule unavailable',
  multi_day_fill: 'multi-day fill',
  lots_unresolvable: 'lots unresolvable',
  broker_resolution_unavailable: 'broker unresolved',
  not_lifecycle_head: 'see first entry',
  charges_not_loaded: 'charges loading',
  broker_direct: 'broker-direct trade',
  broker_truth_incomplete: 'broker charges incomplete',
  not_reported: 'not reported',
};

export function chargeReasonPhrase(reason) {
  if (!reason) return null;
  const text = String(reason);
  if (text.startsWith('no_schedule_row')) return 'no schedule row';
  return CHARGE_REASON_PHRASES[text] || text.replaceAll('_', ' ');
}

const _apiNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * describeOrderCharges — the ONE reader of an order's per-order charge fields.
 * @returns {{state:'amount'|'unavailable'|'none', total:number|null, reason:string|null,
 *   reasonPhrase:string|null, indicative:boolean, source:string|null, partial:boolean,
 *   extrapolated:boolean}}
 */
export function describeOrderCharges(order) {
  const o = order || {};
  const total = _apiNumber(o.charges_total);
  const filledQty = _apiNumber(o.filled_qty);
  const isFilled = String(o.status || '').toUpperCase() === 'FILLED' || (filledQty != null && filledQty > 0);
  const source = o.charges_source ?? null;
  const partial = o.broker_truth_partial === true;
  //! R7-T5 — schedule-derived (and partial broker-truth) charges are Indicative; broker_truth is not.
  const indicative = source === 'broker_truth' && !partial
    ? false
    : (o.indicative === true || source === 'schedule' || partial);
  const base = { source, partial, indicative, extrapolated: o.schedule_extrapolated === true };
  if (total != null) return { ...base, state: 'amount', total, reason: null, reasonPhrase: null };
  const reason = o.charges_unavailable_reason || (isFilled ? 'not_reported' : null);
  if (reason) {
    return { ...base, state: 'unavailable', total: null, reason, reasonPhrase: chargeReasonPhrase(reason) };
  }
  return { ...base, state: 'none', total: null, reason: null, reasonPhrase: null };
}

//--> Breakdown text for a tooltip / secondary line: "brokerage 0.00 · stt_ctt 1.23" (formatting only).
export function chargeBreakdownText(order) {
  const parts = Array.isArray(order?.charges_breakdown) ? order.charges_breakdown : [];
  return parts
    .map((c) => {
      const amount = _apiNumber(c?.amount);
      return amount == null ? null : `${c.name} ${amount.toFixed(2)}`;
    })
    .filter(Boolean)
    .join(' · ');
}

//! A broker-direct pair the server did not stamp (no head from order-view) is honestly unresolved.
export const BROKER_DIRECT_UNRESOLVED_NET = Object.freeze({
  net: null, gross: null, charges: null, grossSource: null, indicative: false, unavailableReason: 'broker_direct',
});

/**
 * readLifecycleNet — the OMS lifecycle net carried by the lifecycle HEAD among a trade group's orders.
 * Always returns a state object; `net` is null with `unavailableReason` set whenever the head is
 * absent or unresolved (an open trade simply never renders it). Only the head renders net (R7-T4);
 * a non-head member group says where the net lives.
 */
export function readLifecycleNet(orders) {
  const members = (orders || []).filter(Boolean);
  //! 144-8 — a terminal entry that never filled has no lifecycle head by design. Classify that
  //  entry outcome here, before the generic non-head fallback, so every card consumer receives
  //  the same display authority and never mistakes an order outcome for unresolved economics.
  const entry = members.find((o) => (o.leg_type || o.action) === 'entry');
  const entryStatus = String(entry?.status || '').toUpperCase();
  const entryFilledQty = _apiNumber(entry?.filled_qty);
  if (entryFilledQty === 0 && (entryStatus === 'REJECTED' || entryStatus === 'CANCELLED')) {
    const rejected = entryStatus === 'REJECTED';
    return {
      net: null,
      gross: null,
      charges: null,
      grossSource: null,
      indicative: false,
      unavailableReason: rejected ? 'rejected_entry' : 'cancelled_entry',
      rejectReason: rejected ? rejectReasonLabel(fullRejectReason(entry)) : null,
    };
  }
  const head = members.find((o) => o.is_lifecycle_head === true);
  if (head) {
    const net = _apiNumber(head.net_pnl);
    return {
      net,
      gross: _apiNumber(head.lifecycle_gross_pnl),
      charges: _apiNumber(head.lifecycle_charges_total),
      grossSource: head.gross_source ?? null,
      indicative: members.some((o) => o.indicative === true || o.broker_truth_partial === true),
      unavailableReason: net == null
        ? (head.net_unavailable_reason || head.charges_unavailable_reason || 'unresolved')
        : null,
    };
  }
  const loaded = members.some((o) => o.is_lifecycle_head !== undefined);
  const reason = members.map((o) => o.net_unavailable_reason).find(Boolean)
    || (loaded ? 'not_lifecycle_head' : 'charges_not_loaded');
  return { net: null, gross: null, charges: null, grossSource: null, indicative: false, unavailableReason: reason };
}

// =============================================================================
// 5. groupOrdersIntoTrades
// =============================================================================

//--> manual-exit-netting-p0 R3.4/G1 — the exit-recognition set for the Trades-tab pairing engine.
//*   A signal-driven / manual / corrective exit with NO parent_order_id pairs against its entry
//*   (orphanExits), instead of being stranded in legacyOrders. Mirrors the backend EXIT_LEG_TYPES
//*   (app/models/order.py) PLUS corrective — drift-locked by tradingTransforms.pairingCoverage.test.js
//*   so a new backend exit leg forces a matching FE update. Bracket children (stop_loss/take_profit)
//*   are already caught by the earlier order.parent_order_id branch, so listing them here is a no-op
//*   for real data and future-proofs the set against a parentless bracket exit.
export const PAIRING_EXIT_LEG_TYPES = new Set([
  'stop_loss',
  'take_profit',
  'rule_exit',
  'time_exit',
  'rollover_exit',
  //--> money-path #30 D4 — the pinned-instrument forced T-1 pre-expiry close (R3.3);
  //*   without this the pairing engine strands a pinned close-out in legacyOrders
  //*   instead of pairing it against its entry.
  'pinned_expiry_exit',
  'flip_exit',
  'manual_exit',
  'corrective',
]);

/**
 * Groups flat orders into TradeGroup objects using parent_order_id.
 * @param {Array} orders - flat array of order objects
 * @returns {Array} TradeGroup[] sorted newest first, open trades at top
 */
export function groupOrdersIntoTrades(orders, openPositions = []) {
  if (!orders || orders.length === 0) return [];

  //--> Build set of (sii, instrument) keys with open positions for cross-reference (D9 — instrument-
  //    scoped so two open MANUAL positions on different instruments never collapse into one).
  const openSids = new Set(
    (openPositions || [])
      .filter((p) => p.status === 'OPEN' || parseFloat(p.qty || 0) > 0)
      .map((p) => _openSidKey(p))
  );

  //--> 0. Ensure every order has _epoch (REST orders may lack it)
  for (const order of orders) {
    if (!order._epoch) {
      const ts = order.created_at || order.timestamp || order.updated_at;
      order._epoch = ts ? new Date(ts).getTime() : 0;
    }
  }

  //--> 1. Identify entry orders by leg_type
  const entryOrders = [];
  const childMap = {};   // parent_order_id -> [orders]
  const orphanExits = []; // signal-driven exits (no parent, has exit_reason)
  const legacyOrders = []; // no leg_type, no parent, no exit_reason (also: all BD rows, see below)

  for (const order of orders) {
    //--> BD (broker-direct) rows are identified EXPLICITLY by leg==='BD' (canonical
    //*   consolidated-view contract — order-view consolidated_view.py
    //*   _map_broker_order_row_to_consolidated always stamps "leg": "BD"). Checked
    //*   FIRST and unconditionally, ahead of legType/parent_order_id/exit_reason:
    //!   a BD row must never be mis-routed into the ALGO entryOrders/childMap/
    //!   orphanExits buckets by an incidental leg_type/action/parent_order_id/
    //!   exit_reason value. Previously BD routing was ONLY implicit — a BD row
    //!   landed in legacyOrders solely because it happened to lack those fields,
    //!   which is fragile: any row that ever carries one of them (a future
    //!   backend field, a stray WS merge) would silently fall through to an
    //!   ALGO-shaped bucket instead of the BD FIFO-pairing engine below.
    if (order.leg === 'BD') {
      legacyOrders.push(order);
      continue;
    }

    const legType = order.leg_type || order.action;

    if (legType === 'entry') {
      entryOrders.push(order);
    } else if (order.parent_order_id) {
      if (!childMap[order.parent_order_id]) childMap[order.parent_order_id] = [];
      childMap[order.parent_order_id].push(order);
    } else if (
      order.exit_reason ||
      PAIRING_EXIT_LEG_TYPES.has(legType)
    ) {
      //--> Signal-driven / manual / corrective exit (no parent_order_id). Accept any exit-class
      //*  leg_type here — signal_consumer tags algo exits 'rule_exit'/'time_exit'; manual exits are
      //*  'manual_exit' (manual-exit-netting-p0 R1); operator repairs are 'corrective' (R2);
      //*  pre-17fbb54 rows had leg_type NULL + exit_reason set. Recognized via PAIRING_EXIT_LEG_TYPES
      //*  (drift-locked to the backend EXIT_LEG_TYPES + corrective). Matching is by
      //*  strategy_instance_id + time window. Without manual_exit/corrective here, R1's netted round
      //*  trip would strand its exit leg in legacyOrders and render unpaired (G1).
      orphanExits.push(order);
    } else if (!legType) {
      legacyOrders.push(order);
    } else {
      //--> Bracket order without parent (shouldn't happen, treat as legacy)
      legacyOrders.push(order);
    }
  }

  //--> 2. Sort entries by time ascending for trade numbering
  entryOrders.sort((a, b) => (a._epoch || 0) - (b._epoch || 0));

  //--> Track newest entry ID per SID for the openSids fallback.
  //*   Without this, the fallback marks ALL entries for the strategy as 'open'
  //*   when any position is live. Only the most recent entry (no filledExit)
  //*   should receive the 'open' status — older entries are closed trades whose
  //*   bracket children may be absent from the REST load window.
  const newestEntryIdBySid = new Map();
  entryOrders.forEach((e) => {
    newestEntryIdBySid.set(_openSidKey(e), e.id || e.order_id);   // D9 — (sii, instrument) keyed
  });

  //--> 3. Build trade groups
  const trades = entryOrders.map((entry, idx) => {
    const entryId = entry.id || entry.order_id;
    const brackets = childMap[entryId] || [];

    //--> Find signal-driven exit for this trade (match by strategy_instance_id + time window)
    const nextEntryTime = idx < entryOrders.length - 1 ? entryOrders[idx + 1]._epoch : Infinity;
    const matchedExits = orphanExits.filter(
      (ex) =>
        ex.strategy_instance_id === entry.strategy_instance_id &&
        (ex._epoch || 0) > (entry._epoch || 0) &&
        (ex._epoch || 0) <= nextEntryTime
    );

    const allExitOrders = [...brackets, ...matchedExits];

    //--> Find the filled exit order (the one that actually closed the position)
    const filledExit = allExitOrders.find((o) => o.status === 'FILLED' && o.side !== entry.side);

    //--> Derive exit reason
    //* Prefer exit_reason (specific: sl/tp/time_exit) over leg_type (generic:
    //* rule_exit groups all signal-driven exits). Bracket children have no
    //* exit_reason, so they fall through to leg_type (stop_loss/take_profit).
    let exitReason = null;
    if (filledExit) {
      const exitKey = filledExit.exit_reason || filledExit.leg_type || filledExit.action;
      exitReason = EXIT_REASON_MAP[exitKey] || 'Closed';
    }

    const entryPrice = parseFloat(entry.filled_price || entry.limit_price || 0);
    const exitPrice = filledExit ? parseFloat(filledExit.filled_price || 0) : null;
    const qty = parseFloat(entry.qty || 1);
    const side = entry.side === 'BUY' ? 'LONG' : 'SHORT';

    //--> Resolve contract_multiplier from the entry order (durable — survives position close)
    const contractMultiplier = parseInt(entry.contract_multiplier || 1, 10);

    //! 144-6 R7-T4 — the closed-trade headline is the OMS lifecycle net_pnl carried by the lifecycle
    //  HEAD order of this group; the browser computes no gross, net or charge allocation.
    const lifecycleNet = readLifecycleNet([entry, ...allExitOrders]);

    return {
      id: entryId,
      tradeNumber: idx + 1,
      entryOrder: entry,
      bracketOrders: allExitOrders,
      //--> D1: Lift instrument fields to trade-group level so TradeCard can render
      //*   instrument header without reaching into entryOrder.instrument_id (UUID).
      //*   order-instrument-identity-normalization P2b — entry.display_symbol (the stored canonical
      //*   instrument-master name) wins first when present; falls back to entry.symbol (the #4b
      //*   read-lane resolve), then entry.underlying_symbol (order-instrument-identity-surfacing —
      //*   the persisted underlying name) so ALGO/paper cards read "CRUDEOIL" instead of "—" when
      //*   neither display_symbol nor the order-view resolver has populated a value, then name.
      symbol:   entry.display_symbol ?? entry.symbol   ?? entry.underlying_symbol   ?? entry.name   ?? null,
      name:     entry.name     ?? null,
      exchange: entry.exchange  ?? null,
      //--> Determine trade status
      //*   1. filledExit found → definitely closed
      //*   2. No filledExit + newest entry for this SID + open position → open
      //*   3. Everything else → closed (earlier trades, missing bracket data, etc.)
      status: filledExit
        ? 'closed'
        : (openSids.has(_openSidKey(entry)) &&
           newestEntryIdBySid.get(_openSidKey(entry)) === entryId)
          ? 'open'
          : 'closed',
      //--> For closed trades without filledExit, derive exitReason from any matched orphan.
      //*   Condition: no filledExit AND this entry is actually 'closed' (not the live one)
      //*   AND there are orphan exit orders covering this trade's time window.
      exitReason: exitReason || (
        !filledExit && matchedExits.length > 0 &&
        (newestEntryIdBySid.get(_openSidKey(entry)) !== entryId || !openSids.has(_openSidKey(entry)))
          ? EXIT_REASON_MAP[matchedExits[0].exit_reason] || 'Closed'
          : null
      ),
      side,
      entryPrice,
      exitPrice,
      pnl: lifecycleNet ? lifecycleNet.net : null,
      lifecycleNet,
      qty,
      contractMultiplier,
      //--> Prefer bar_start (fill-bar epoch) over _epoch (wall-clock event-publish
      //*   time). Under tick-mode paper fills, trigger_consumer advances bar_start
      //*   to the fill tick's bar so chart markers + bracket bands anchor to the
      //*   entry candle, not the signal-arrival candle. Pre-tick-mode fallback:
      //*   _epoch preserves the old behavior when bar_start is missing.
      entryTime: _barStartOrEpoch(entry),
      exitTime: filledExit ? _barStartOrEpoch(filledExit) : null,
      duration: filledExit ? _formatDuration(_barStartOrEpoch(entry), _barStartOrEpoch(filledExit)) : null,
    };
  });

  //--> 4. BD (broker-direct) trades.
  //   A row is broker-direct when order.leg === 'BD' (canonical consolidated contract)
  //   OR when it fell into legacyOrders (no leg_type, no parent_order_id, no exit_reason,
  //   no strategy_instance_id). These rows were previously bucketed into legacyOrders and
  //   silently discarded (never returned).
  //
  //   §4b split: EXECUTED rows (FILLED/EXECUTED/COMPLETE, numeric qty, a resolvable
  //   price) are FIFO-pairing-eligible — reconstructed into real entry+exit round
  //   trips (or an open remainder) by _buildBdPairedGroups. Everything else
  //   (CANCELLED, missing price, etc.) keeps the original single-row degenerate
  //   mapping below: P&L sourced from the row's broker-provided realized_pnl /
  //   unrealized_pnl (not computed from price math), no bracket pairing.
  const bdExecutedRows = [];
  const bdOtherRows = [];
  for (const row of legacyOrders) {
    (_isExecutedBdRow(row) ? bdExecutedRows : bdOtherRows).push(row);
  }

  const bdDegenerateGroups = bdOtherRows.map((row) => {
    const rowId = row.order_id || row.id;
    const entryTime = row._epoch || (row.created_at ? new Date(row.created_at).getTime() : 0);
    const rowStatus = (() => {
      const s = (row.status || '').toUpperCase();
      //! #10494-23 lane C — terminalised broker rows (EXPIRED/CANCELLED/REJECTED)
      //  must never render as 'open': a dead prior-day order is not a live trade.
      //  Extends the one existing closed/open mapping here rather than adding a
      //  second status map (per 02-fix-design.md AC-6/AC-26).
      if (s === 'FILLED' || s === 'COMPLETE' || s === 'COMPLETED') return 'closed';
      if (s === 'EXPIRED' || s === 'CANCELLED' || s === 'REJECTED') return 'closed';
      return 'open';
    })();
    const rowSide = row.side === 'BUY' ? 'LONG' : row.side === 'SELL' ? 'SHORT' : (row.side ?? 'LONG');
    return {
      id: rowId,
      tradeNumber: null,          // BD rows have no sequential trade number
      entryOrder: row,            // single-row: the BD order itself
      bracketOrders: [],          // BD orders have no algo bracket legs
      //--> D1: lift instrument fields from the BD row directly
      //*   order-instrument-identity-normalization P2b — display_symbol first (see closedGroups
      //*   above); underlying_symbol fallback (order-instrument-identity-surfacing) — see §4a.
      //!   NOT one of the 4 sites cited in the P2b work package (668/699/744/932 pre-edit line
      //!   numbers) — added here too because this is the byte-identical fallback-chain defect on
      //!   the same object shape (an order row); leaving it out would mean BD single-row trade
      //!   cards keep ignoring display_symbol while every sibling card honors it. Flagged in
      //!   03-P2b-impl.md for eng-tech-lead visibility.
      symbol:   row.display_symbol ?? row.symbol   ?? row.underlying_symbol   ?? row.name   ?? null,
      name:     row.name     ?? null,
      exchange: row.exchange  ?? null,
      status:     rowStatus,
      exitReason: null,
      side:       rowSide,
      entryPrice: parseFloat(row.filled_price || row.price || 0),
      exitPrice:  null,
      //--> D4: BD P&L sourced from broker-provided fields, never computed from entry-vs-exit math
      pnl:        row.realized_pnl ?? row.unrealized_pnl ?? null,
      qty:        parseFloat(row.qty || 1),
      contractMultiplier:    parseInt(row.contract_multiplier || 1, 10),
      entryTime,
      exitTime:   null,
      duration:   null,
      isBrokerDirect: true,
      source_tag: row.source_tag ?? 'EXTERNAL',
    };
  });

  //--> §4b — FIFO-pair the EXECUTED BD rows into real entry+exit round trips
  //*   (plus at most one open remainder per bucket), anchored against openPositions.
  const bdPairedGroups = _buildBdPairedGroups(bdExecutedRows, openPositions);

  const bdTradeGroups = [...bdDegenerateGroups, ...bdPairedGroups];

  //--> 5. Merge ALGO + BD groups, then sort: open first, then time descending
  const allTrades = [...trades, ...bdTradeGroups];
  allTrades.sort((a, b) => {
    if (a.status === 'open' && b.status !== 'open') return -1;
    if (b.status === 'open' && a.status !== 'open') return 1;
    return (b.entryTime || 0) - (a.entryTime || 0);
  });

  return allTrades;
}

// =============================================================================
// 5b. buildOpenPositionFallbackTrade — trades-view-strategy-backfill (issue #3)
// =============================================================================

//--> groupOrdersIntoTrades can only synthesize a TradeGroup by anchoring on an
//*   ENTRY order present in the `orders` array it was given (see entryOrders.map
//*   above). If that entry order is missing entirely — the order-window-
//*   starvation symptom: a strategy's own orders aged out of the shared
//*   global-100 fetch (TradingDataContext.jsx:838/:922-933) before the
//*   per-strategy backfill (useStrategyOrdersBackfill) resolves, or the entry
//*   pre-dates whatever history window the backend still holds — trades comes
//*   back [] even though the strategy has a live OMS position. Rather than let
//*   TradeHistory fall through to "No trades yet", this builds a TradeGroup-
//*   shaped object directly from the position snapshot so the operator still
//*   sees they are IN a trade.
/**
 * Synthesizes a TradeGroup-shaped object from a single OMS position row, for
 * the case where there is no backing entry order to pair (groupOrdersIntoTrades
 * returned no trade for this position). The position is authoritative on its
 * own — side/qty/avg price all come from the OMS book, not from order math.
 * entryOrder is deliberately left null: callers (TradeCard) must not render
 * order-leg-dependent UI (SL/TP bar, expandable order table) from this shape,
 * only the isPositionOnly flag distinguishes it from a "real" empty groups.
 * @param {object} position - a single position row (expected leg==='ALGO')
 * @returns {object|null} TradeGroup-shaped object, or null if qty is not > 0
 */
export function buildOpenPositionFallbackTrade(position) {
  if (!position) return null;
  const qty = parseFloat(position.qty ?? position.quantity ?? 0);
  if (!(qty > 0)) return null;

  //--> Normalize side to the LONG/SHORT vocabulary TradeCard expects (position
  //*   rows may carry the raw broker side BUY/SELL instead).
  const rawSide = String(position.side || '').toUpperCase();
  const side = rawSide === 'LONG' || rawSide === 'BUY' ? 'LONG'
    : rawSide === 'SHORT' || rawSide === 'SELL' ? 'SHORT'
    : (position.side || 'LONG');

  const entryPrice = parseFloat(
    position.avg_entry_price ?? position.avg_cost ?? position.entry_price ?? 0
  );
  const contractMultiplier = parseInt(position.contract_multiplier || 1, 10);
  const entryTimeSrc = position.created_at || position.opened_at || null;

  return {
    id: `position-fallback:${position.strategy_instance_id || position.instrument_id || 'unknown'}`,
    tradeNumber: null,
    entryOrder: null,     // no backing order row — see isPositionOnly below
    bracketOrders: [],
    //--> D1-style instrument lift, mirrored from the entry-anchored branch above.
    //*   instrument-id-display-remediation S8 — display_symbol-first (the canonical name now stamped
    //*   on positions and surfaced via AlgoPositionSummary), then the resolved symbol chain; never "—"
    //*   for a starved-out strategy whose position carries a display_symbol.
    symbol:   position.display_symbol ?? position.symbol   ?? position.instrument_symbol ?? position.name ?? null,
    name:     position.name     ?? null,
    exchange: position.exchange ?? null,
    status: 'open',
    exitReason: null,
    side,
    entryPrice,
    exitPrice: null,
    //--> D4-style: P&L sourced from the OMS snapshot, never computed from
    //*   price math we don't have an entry order to compute against.
    pnl: position.unrealized_pnl ?? null,
    qty,
    contractMultiplier,
    entryTime: entryTimeSrc ? new Date(entryTimeSrc).getTime() : null,
    exitTime: null,
    duration: null,
    //! Distinguishes this synthetic card from a real (entry-anchored) trade
    //! group. Not to be confused with isBrokerDirect — this is an ALGO/MANUAL
    //! position missing its entry order, NOT a broker-direct row.
    isPositionOnly: true,
    //--> Carries the source position row so callers (TradeHistory) can resolve
    //*   "the position behind this card" directly instead of routing through
    //*   resolvePositionForEntry(positions, entryOrder) — which needs an
    //*   entryOrder we don't have here.
    _position: position,
  };
}

// =============================================================================
// 6. SOURCE_TAG_BADGE + getSourceTagBadge  (R102.3)
// =============================================================================

/**
 * SOURCE_TAG_BADGE — per source-tag badge config.
 * Colors use Tailwind at /15 bg, /20 border to match the STATUS_STYLES palette
 * in OrdersTable.jsx.
 * FOREIGN has NO entry — it is screened at the endpoint and never reaches the UI.
 */
export const SOURCE_TAG_BADGE = {
  ALGO:         { label: 'Algo',     variant: 'info'    },
  MANUAL:       { label: 'Manual',   variant: 'profit'  },
  EXTERNAL:     { label: 'External', variant: 'warn'    },
  UNATTRIBUTED: { label: 'Unknown',  variant: 'neutral' },
  //! R7.2 — broker-truth (BD) positions on the Positions tab. Additive; consumed ONLY by the new
  //  PositionsTable Source column (which passes `leg`, not `source_tag`). No Orders-page source_tag
  //  value is ever 'BD' (broker-only ORDER rows use EXTERNAL), so this never affects the orders leg.
  BD:           { label: 'Broker',   variant: 'neutral' },
  //! #10479 D3 — order-view derives SYSTEM for a cron/sweep flatten stamped orders.source=MANUAL
  //  with exit_reason=pre_squareoff_flatten (consolidated_view.py `_display_source`). Additive;
  //  an operator-typed admin force-close keeps MANUAL (unchanged) since that lane IS a manual action.
  SYSTEM:       { label: 'System',   variant: 'neutral' },
  // FOREIGN: never rendered — screened at the gateway endpoint before reaching frontend
};

/**
 * getSourceTagBadge — returns the badge config for a source_tag value.
 * Falls back to UNATTRIBUTED for missing / unknown tags.
 * Never returns a FOREIGN entry.
 *
 * @param {string|null|undefined} tag
 * @returns {{ label: string, variant: string }}
 */
export const getSourceTagBadge = (tag) =>
  SOURCE_TAG_BADGE[tag] || SOURCE_TAG_BADGE.UNATTRIBUTED;

// =============================================================================
// 6a. PRICE_SOURCE_BADGE + getPriceSourceBadge  (algo-entry-null-limit-price D2)
// =============================================================================

/**
 * PRICE_SOURCE_BADGE — per price-source badge config, cloned from SOURCE_TAG_BADGE's shape
 * (`:1188-1198`). Surfaces the `orders.price_source` provenance column (order.py:503-514,
 * already live-populated for the exit lane; R1/R2 stamp it for entries too) so a human — not just
 * SQL — can tell whether a price was typed, derived from LTP, derived from the order-book, or
 * band-clamped (SEBI audit distinguishability, operator decision 3).
 *
 * Entry-scoped values carry an `entry_*` prefix (R1) — kept as DISTINCT keys from the exit-scoped
 * values below (never collapsed to a shared "fresh"/"derived" label) so entry-vs-exit is
 * reconstructable from the value itself, matching the backend's own value-set split.
 *
 * !! Stage-4 QA A/B loop-back (iter-2 BLOCKER 2, algo-entry-null-limit-price) — every label below
 * !! is chosen to be TEXTUALLY DISTINCT from SOURCE_TAG_BADGE's order-source labels (Algo/Manual/
 * !! External/Unknown/Broker, `:1189-1196`), which render in the SAME Orders row (a different
 * !! column — see OrdersTable.jsx's `leg` cell vs `source_tag` cell). The prior bare "Manual" (this
 * !! badge) vs "Manual" (SOURCE_TAG_BADGE.MANUAL) collision made the two badges indistinguishable
 * !! and regressed the pre-existing Orders/drawer tests. The exit-derived labels below carry a
 * !! `Px: ` qualifier for the same reason (a bare "Rejected" would also read as an order/leg status,
 * !! not a price-derivation state).
 */
export const PRICE_SOURCE_BADGE = {
  // Entry-derived (R1/R2) — the NEW server-side deriver this fix adds. Short domain tokens
  // (LTP+off / OB bid / OB ask) that do not collide with any order-source/leg-type/status label
  // anywhere else in the app — no additional qualifier needed.
  entry_ltp: { label: 'LTP+off', variant: 'info' },
  entry_orderbook_bid: { label: 'OB bid', variant: 'info' },
  entry_orderbook_ask: { label: 'OB ask', variant: 'info' },
  // 10509-15 R2/R6 — entry falls back off its chosen order-book reference when the book side is
  // unavailable at the stamping instant (the shared `order_pricing.normalize_order_pricing`
  // `defaults_filled` lane). Distinct from `entry_ltp` (a stamp-time reference CHOICE) — this one
  // records that the choice could not be honored and LTP was substituted.
  entry_ltp_fallback: { label: 'LTP (OB fallback)', variant: 'warn' },
  // Exit-derived (pre-existing marketable-limit conversion, `derive_marketable_limit` —
  // live-populated in prod today: fresh=55, last_known_max_aggression=2, rejected=22 rows at
  // design time) — `Px: ` qualified (never bare "Fresh"/"Rejected", which would collide visually
  // with an order status or leg-type label) — distinct labels, never reused for an entry value.
  fresh: { label: 'Px: fresh', variant: 'profit' },
  last_known_max_aggression: { label: 'Px: max-agg', variant: 'warn' },
  rejected: { label: 'Px: rejected', variant: 'loss' },
  price_untrusted_not_dispatched: { label: 'Px: untrusted', variant: 'loss' },
  // 10509-15 R3/R6 — every exit now inherits its reference at the ONE chokepoint
  // (resolve_exit_reference); these 4 values are its possible stamped provenances, each
  // `Px: `-qualified and textually distinct from both the entry tokens above and each other.
  exit_ltp: { label: 'Px: exit LTP', variant: 'info' },
  exit_ltp_fallback: { label: 'Px: exit LTP (OB fallback)', variant: 'warn' },
  exit_orderbook_bid: { label: 'Px: exit OB bid', variant: 'info' },
  exit_orderbook_ask: { label: 'Px: exit OB ask', variant: 'info' },
  // Tick-derived fallback (fill_expectation_watchdog chase anchor falls back to the last tick when
  // no fresh LTP/order-book quote is available) — distinct from `last_known_max_aggression`, which
  // is a CHASE STRATEGY outcome, not a stamped price-source provenance.
  exit_tick_fallback: { label: 'Px: exit tick fallback', variant: 'warn' },
};

//! Stage-4 QA A/B loop-back (iter-2 BLOCKER 2) — was bare `{ label: 'Manual', ... }`, textually
//! IDENTICAL to SOURCE_TAG_BADGE.MANUAL's "Manual" (`:1190`) — two indistinguishable "Manual"
//! badges rendered in the same Orders row (order-source column + price-source column). `Price: `
//! qualifies it so it reads as "the PRICE was manually typed", never confusable with the
//! order-source "Manual" badge. variant stays 'neutral' (already distinct from MANUAL's 'profit').
const PRICE_SOURCE_MANUAL_BADGE = { label: 'Price: Manual', variant: 'neutral' };
const PRICE_SOURCE_BAND_CLAMPED_BADGE = { label: 'Band-clamped', variant: 'warn' };

/**
 * getPriceSourceBadge — resolves an order's price-derivation provenance to a badge config.
 * `price_source` NULL/undefined (a genuinely manually-typed price, never converted) -> "Price:
 * Manual" (Stage-4 QA loop-back — NOT bare "Manual", which collided with SOURCE_TAG_BADGE.MANUAL).
 * An unrecognized value falls back to "Price: Manual" too (honest — never fabricates a derivation
 * the badge map doesn't know about).
 *
 * `band_clamped=true` overrides the top-level label to "Band-clamped" (the ±band safety bound
 * fired) — orthogonal to `price_source` (fires on either the entry or exit lane), per R1's
 * documented broadened post-condition semantics. The underlying source is NOT lost: it rides back
 * as `sourceLabel` + `clamped:true` so a caller wanting the full SEBI-audit picture (e.g. the
 * order detail drawer, or a compact secondary tag on the orders table) can still show "clamped
 * from LTP+offset" rather than only the generic clamp badge — entry-vs-exit stays distinguishable
 * via the underlying `price_source` value even when the TOP badge reads "Band-clamped".
 *
 * @param {string|null|undefined} price_source
 * @param {boolean|null|undefined} band_clamped
 * @returns {{label: string, variant: string, clamped: boolean, sourceLabel?: string}}
 */
export const getPriceSourceBadge = (price_source, band_clamped) => {
  const base = price_source ? (PRICE_SOURCE_BADGE[price_source] || PRICE_SOURCE_MANUAL_BADGE) : PRICE_SOURCE_MANUAL_BADGE;
  if (band_clamped === true) {
    return { ...PRICE_SOURCE_BAND_CLAMPED_BADGE, clamped: true, sourceLabel: base.label };
  }
  return { ...base, clamped: false };
};

//! 10509-6/E13/D31 — a standing SL row's current trigger sits at/beyond the entry fill price in
//! the profit direction (derived server-side, `lock_state` on the order row / open-trade
//! `stop_loss`). Display-only chip, same idiom as PRICE_SOURCE_BAND_CLAMPED_BADGE above — never
//! re-derived client-side.
const LOCK_STATE_BADGE = {
  BE: { label: 'BE', variant: 'info' },
  LOCKED: { label: 'Locked', variant: 'profit' },
};

/**
 * getLockStateBadge — resolves an SL row's `lock_state` ("BE" | "LOCKED" | null/absent) to a
 * badge config, or null when there is nothing to show (no chip rendered).
 * @param {string|null|undefined} lock_state
 * @returns {{label: string, variant: string}|null}
 */
export const getLockStateBadge = (lock_state) => LOCK_STATE_BADGE[lock_state] || null;

// =============================================================================
// 6b. BROKER_BADGE + getBrokerBadge  (R3.2)
// =============================================================================

/**
 * BROKER_BADGE — per-broker badge config. Keyed by the UPPERCASED broker value
 * (routed_broker_id / broker_name), mirroring SOURCE_TAG_BADGE's shape.
 */
export const BROKER_BADGE = {
  FLATTRADE: { label: 'Flattrade', variant: 'info'    },
  ICICI:     { label: 'ICICI',     variant: 'profit'  },
  PAPER:     { label: 'Paper',     variant: 'neutral' },
};

//! Broker values reach the FE in TWO conventions (MF-2 hollow-green fix): R3.1's coalesce yields the
//  routing-triplet form (`icici`/`flattrade`), while the broker-swept (BD) leg carries the canonical
//  `brokers.broker_name` (`icici_direct`, `papertrade`, …). Normalize BOTH to a badge key — strip a
//  trailing `_direct`, then alias — so a real BD row (`icici_direct`) resolves to the ICICI badge
//  instead of a blank em-dash. Genuinely-unmapped/missing brokers still return null (honest em-dash).
const _BROKER_ALIASES = {
  icici: 'ICICI', breeze: 'ICICI',
  flattrade: 'FLATTRADE', noren: 'FLATTRADE',
  paper: 'PAPER', papertrade: 'PAPER',
};

/**
 * getBrokerBadge — badge config for a broker value across both broker-value conventions, or null
 * when the broker is missing/genuinely-unmapped so the caller renders an em-dash, NEVER a broken/
 * empty badge (R3.2's own AC).
 *
 * @param {string|null|undefined} broker
 * @returns {{ label: string, variant: string }|null}
 */
export const getBrokerBadge = (broker) => {
  const base = String(broker || '').trim().toLowerCase().replace(/_direct$/, '');
  if (!base) return null;
  const key = _BROKER_ALIASES[base] || base.toUpperCase();
  return BROKER_BADGE[key] || null;
};

// =============================================================================
// 6c. _computeTatLatency  (R4.2/R4.3) — pure, exported for testability
// =============================================================================

/**
 * _computeTatLatency — duration in ms between two ISO timestamps, or { unavailable: true } when a
 * value is missing / invalid / a broker sentinel / implausible. Operands are parsed with
 * `new Date(iso)` (embedded-offset aware) — NEVER a naive string diff — so a UTC-vs-IST pair for the
 * SAME instant yields 0ms, not a ±5:30h artifact (R4.2's single most dangerous failure mode).
 *
 * @param {string|null|undefined} startIso
 * @param {string|null|undefined} endIso
 * @param {string|null|undefined} orderCreatedAtIso — plausibility anchor (a stamp far before this is bogus)
 * @returns {{ ms: number } | { unavailable: true }}
 */
export function _computeTatLatency(startIso, endIso, orderCreatedAtIso) {
  if (!startIso || !endIso) return { unavailable: true };
  const start = new Date(startIso);
  const end = new Date(endIso);
  const created = orderCreatedAtIso ? new Date(orderCreatedAtIso) : null;
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return { unavailable: true };
  //! R4.3 — Noren/broker sentinel (1980-01-01, any zone) → unavailable, never a multi-decade number.
  const isSentinel = (d) => d.getUTCFullYear() === 1980 && d.getUTCMonth() === 0 && d.getUTCDate() === 1;
  if (isSentinel(start) || isSentinel(end)) return { unavailable: true };
  if (end.getTime() < start.getTime()) return { unavailable: true };                       // negative
  if (created && start.getTime() < created.getTime() - 1000) return { unavailable: true };  // implausibly pre-order
  return { ms: end.getTime() - start.getTime() };
}

/**
 * _buildOrderTimeline — the exact #118 seven-point order timeline plus its six adjacent hops.
 * Hops are computed only by _computeTatLatency; missing, invalid, sentinel, negative and
 * implausible pairs therefore stay unavailable rather than being fabricated by the UI.
 */
export function _buildOrderTimeline(order = {}) {
  const points = [
    { key: 'signal_generated_at', label: 'Signal generated', value: order.signal_generated_at },
    { key: 'created_at', label: 'OMS accepted', value: order.created_at },
    { key: 'admitted_at', label: 'Admission decided', value: order.admitted_at },
    { key: 'order_constructed_at', label: 'Order constructed', value: order.order_constructed_at },
    { key: 'sent_at', label: 'Sent to broker', value: order.sent_at },
    { key: 'routed_at', label: 'Broker acknowledged', value: order.routed_at },
    // --> 10509-21 C16 — source is explicit; legacy timestamps never manufacture a label.
    { key: 'exchange_fill_timestamp', label: 'Fill / terminal',
      value: order.fill_time_source ? order.bar_start : order.exchange_fill_timestamp,
      source: order.fill_time_source ?? null },
  ];
  const hops = points.slice(0, -1).map((point, index) => {
    const nextPoint = points[index + 1];
    // --> Detection/reconciliation time is display-only here; broker latency still ends at the
    //     real exchange timestamp and is unavailable when no such timestamp exists.
    const latencyEnd = nextPoint.key === 'exchange_fill_timestamp'
      ? order.exchange_fill_timestamp
      : nextPoint.value;
    return {
      from: point.key,
      to: nextPoint.key,
      latency: _computeTatLatency(
        point.value,
        latencyEnd,
        point.key === 'signal_generated_at' ? null : order.created_at,
      ),
    };
  });
  return { points, hops };
}

// =============================================================================
// 7. truncateId + resolveSourceTag (Orders dual-ID cell + lean-WS source)
// =============================================================================

/**
 * truncateId — shorten a long id for compact display, keeping the head + an ellipsis.
 * Short ids (<= head + 1) are returned unchanged. Null/undefined → ''.
 * @param {string|null|undefined} id
 * @param {number} head  characters to keep from the start (default 8)
 */
export const truncateId = (id, head = 8) => {
  if (!id) return '';
  const s = String(id);
  return s.length <= head + 1 ? s : `${s.slice(0, head)}…`;
};

/**
 * resolveSourceTag — pick the effective source-tag for the badge.
 * The WS lane carries source_tag='UNATTRIBUTED' (present) plus a human `source` (ALGO/MANUAL);
 * a bare `??` cannot fall through the present 'UNATTRIBUTED', so prefer a real source_tag and
 * otherwise fall back to `source`. Returns an UPPERCASE tag; 'UNATTRIBUTED' when nothing resolves.
 * @param {{source_tag?: string, source?: string}} order
 * @returns {string}
 */
export const resolveSourceTag = (order = {}) => {
  const tag = order.source_tag;
  if (tag && tag !== 'UNATTRIBUTED') return String(tag).toUpperCase();
  const src = order.source;
  if (src) {
    //! #10479 D3 — the WS order lane carries the raw policy column `source` (MANUAL for a cron
    //  flatten) and no consolidated `source_tag`, so apply the SAME display rule order-view's
    //  `_display_source()` applies on the REST lane: MANUAL + exit_reason=pre_squareoff_flatten
    //  is a SYSTEM close, not an operator click. One label for both lanes.
    if (String(src).toUpperCase() === 'MANUAL' && order.exit_reason === 'pre_squareoff_flatten') return 'SYSTEM';
    return String(src).toUpperCase();
  }
  return String(tag || 'UNATTRIBUTED').toUpperCase();
};

// =============================================================================
// 8. getOrderWorkingPrice + resolveCurrentBracketLevels (#10472 WP3, R6) —
//    single working-price / current-bracket-level authority. Extracted from the
//    inline `price ?? limit_price ?? trigger_price` duplicated at
//    OrdersTable.jsx:491 and MobileOrders.jsx:141; also backs the TradeCard
//    SL/TP bar + leg-row Price column so the dashboard card and the Orders
//    page read the SAME current leg price instead of a construction-time copy.
// =============================================================================

/**
 * getOrderWorkingPrice — the price an order is CURRENTLY resting at (not filled_price).
 * A plain order carries `price`; OMS bracket/exit legs carry `limit_price` and/or
 * `trigger_price`. Null/undefined/'' → null; otherwise coerced to Number.
 * @param {{price?: *, limit_price?: *, trigger_price?: *}} o
 * @returns {number|null}
 */
export const getOrderWorkingPrice = (o) => {
  const v = o?.price ?? o?.limit_price ?? o?.trigger_price;
  return v == null || v === '' ? null : Number(v);
};

//--> Bracket-leg statuses that no longer represent the "current" protective level — a chase
//*   amend, a broker reject, a fill or an expiry supersedes the leg, so it must never win over a
//*   still-live leg of the same type (QA F-INV-19).
const _SUPERSEDED_LEG_STATUSES = new Set(['CANCELLED', 'REJECTED', 'FILLED', 'EXPIRED']);

/**
 * resolveCurrentBracketLevels — the CURRENT SL/TP price a position is protected by, derived
 * from the bracket leg order rows themselves (same rows OrdersTable/TradeCard's leg table
 * render), not a construction-time copy. Picks the newest (`_epoch`) non-CANCELLED/REJECTED
 * leg per type: SL level = `trigger_price` (the stop level the market must reach), falling
 * back to the working price only when the leg carries no trigger; TP level = the working price.
 * @param {Array<{leg_type?: string, exit_reason?: string, status?: string, _epoch?: number,
 *   price?: *, limit_price?: *, trigger_price?: *}>} bracketOrders
 * @returns {{sl: number|null, tp: number|null}}
 */
export function resolveCurrentBracketLevels(bracketOrders = []) {
  let slLeg = null;
  let tpLeg = null;
  for (const leg of bracketOrders || []) {
    if (!leg || _SUPERSEDED_LEG_STATUSES.has(leg.status)) continue;
    const legType = leg.leg_type || leg.exit_reason;
    const epoch = leg._epoch || 0;
    if (legType === 'stop_loss' && (!slLeg || epoch >= (slLeg._epoch || 0))) slLeg = leg;
    else if (legType === 'take_profit' && (!tpLeg || epoch >= (tpLeg._epoch || 0))) tpLeg = leg;
  }
  const slTrigger = slLeg?.trigger_price;
  const sl = !slLeg ? null
    : (slTrigger == null || slTrigger === '') ? getOrderWorkingPrice(slLeg)
    : Number(slTrigger);
  const tp = tpLeg ? getOrderWorkingPrice(tpLeg) : null;
  return { sl, tp };
}

/**
 * pickLevelPair — ONE gating rule for every SL/TP display surface (TradeCard bar, leg table,
 * SignalEvaluationPanel): the first candidate {sl, tp} pair with at least one resolvable level
 * wins AS A PAIR — a missing side stays null rather than being mixed with a lower-authority
 * source (a live SL leg next to a stale entry-copy TP would misstate what protects the position).
 * @param {...({sl?: number|null, tp?: number|null}|null|undefined)} candidates — highest authority first
 * @returns {{sl: number|null, tp: number|null}}
 */
export function pickLevelPair(...candidates) {
  for (const c of candidates) {
    const sl = Number.isFinite(c?.sl) ? c.sl : null;
    const tp = Number.isFinite(c?.tp) ? c.tp : null;
    if (sl != null || tp != null) return { sl, tp };
  }
  return { sl: null, tp: null };
}

export { EXIT_REASON_MAP, LEG_TYPE_DISPLAY, DIAGNOSIS_STATE_LABELS, _formatDuration, _fmtTimeIST, _fmtPrice, formatExitConversion, _istDateKey, _istTimeFmt };
